import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../lib/api';
import { useActiveCompany } from './CompanySwitcher';
import { Icon, type IconName } from './icons';
import { HELP_TOPICS } from '../pages/Help';

interface Item {
  id: string;
  label: string;
  hint?: string;
  icon: IconName;
  group: string;
  run: () => void;
}

/**
 * Search forms and run commands from one field.
 *
 * Two lists rather than one so a form named "Team" cannot be buried under the Team page:
 * forms are what people look for by name, commands are what they look for by verb, and
 * ranking them against each other makes both worse.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { companies, active, select } = useActiveCompany();
  const [term, setTerm] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Only while the palette is open: this is a search box, not a background sync.
  const forms = useQuery({
    queryKey: ['forms', 'palette', active?.id ?? 'all'],
    queryFn: () => api.forms.list(active ? { companyId: active.id } : undefined),
    enabled: open,
    staleTime: 30_000,
  });

  const items = useMemo<Item[]>(() => {
    const query = term.trim().toLowerCase();
    const matches = (text: string) => text.toLowerCase().includes(query);

    const formItems: Item[] = (forms.data?.data ?? [])
      .filter((f) => !query || matches(f.name))
      .slice(0, 6)
      .map((f) => ({
        id: `form-${f.id}`,
        label: f.name,
        hint: f.status === 'Published' ? 'Live' : f.status,
        icon: 'grid',
        group: 'Forms',
        run: () => navigate(`/forms/${f.id}`),
      }));

    const pages: { label: string; to: string; icon: IconName }[] = [
      { label: 'All forms', to: '/', icon: 'grid' },
      { label: 'Responses', to: '/responses', icon: 'checkbox' },
      { label: 'Companies', to: '/companies', icon: 'building' },
      { label: 'Themes', to: '/themes', icon: 'palette' },
      { label: 'Team', to: '/team', icon: 'users' },
      { label: 'API keys', to: '/api-keys', icon: 'ticket' },
      { label: 'Audit log', to: '/audit', icon: 'shield' },
      { label: 'Your account', to: '/account', icon: 'shield' },
    ];

    const commandItems: Item[] = [
      ...pages
        .filter((p) => !query || matches(p.label))
        .map((p) => ({
          id: `go-${p.to}`,
          label: `Go to ${p.label}`,
          icon: p.icon,
          group: 'Commands',
          run: () => navigate(p.to),
        })),
      ...(!query || matches('new form')
        ? [{
            id: 'new-form',
            label: 'New form',
            icon: 'plus' as IconName,
            group: 'Commands',
            run: () => navigate('/?new=1'),
          }]
        : []),
      ...(!query || matches('theme') || matches('dark') || matches('light')
        ? [{
            id: 'toggle-theme',
            label: 'Switch colour theme',
            icon: 'sun' as IconName,
            group: 'Commands',
            run: cycleTheme,
          }]
        : []),
    ];

    // Documentation is searchable from here because that is where somebody who cannot find
    // something looks first — and a help section nobody can find is no help at all.
    const helpItems: Item[] = HELP_TOPICS
      .filter((t) => !query || matches(t.title) || matches(t.blurb))
      .map((t) => ({
        id: `help-${t.slug}`,
        label: t.title,
        hint: 'Docs',
        icon: t.icon,
        group: 'Help',
        run: () => navigate(`/help/${t.slug}`),
      }));

    // Switching company is a command too — it is the thing that changes what everything
    // else lists, and hunting for the header control to do it is the slower path.
    const companyItems: Item[] = companies
      .filter((c) => c.id !== active?.id && (!query || matches(c.name)))
      .slice(0, 4)
      .map((c) => ({
        id: `company-${c.id}`,
        label: `Switch to ${c.name}`,
        icon: 'building' as IconName,
        group: 'Companies',
        run: () => { select(c.id); navigate('/'); },
      }));

    return [...formItems, ...commandItems, ...companyItems, ...helpItems];
  }, [term, forms.data, companies, active, navigate, select]);

  // A changing result set must not leave the highlight pointing past the end.
  useEffect(() => { setCursor(0); }, [term]);

  useEffect(() => {
    if (!open) return;
    setTerm('');
    setCursor(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  if (!open) return null;

  const choose = (item: Item | undefined) => {
    if (!item) return;
    item.run();
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (items.length ? (c + 1) % items.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (items.length ? (c - 1 + items.length) % items.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(items[cursor]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  let lastGroup = '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/45 px-4 pt-[12vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search forms or run a command"
        className="w-full max-w-[560px] overflow-hidden rounded-lg border
                   border-[var(--ff-border)] bg-[var(--ff-surface)] shadow-[var(--ff-shadow-lg)]"
      >
        <div className="flex items-center gap-2.5 border-b border-[var(--ff-border)] px-3.5 py-3">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search forms or run a command"
            aria-label="Search forms or run a command"
            aria-controls="ff-palette-list"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none
                       placeholder:text-[var(--ff-text-faint)]"
          />
          <kbd className="ff-mono rounded border border-[var(--ff-border)]
                          bg-[var(--ff-surface-sunken)] px-1.5 py-0.5 text-[11px]
                          text-[var(--ff-text-faint)]">
            esc
          </kbd>
        </div>

        <ul
          ref={listRef}
          id="ff-palette-list"
          role="listbox"
          aria-label="Results"
          className="max-h-[52vh] overflow-auto p-1.5"
        >
          {items.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-[var(--ff-text-muted)]">
              {forms.isLoading ? 'Searching…' : `Nothing matches “${term}”`}
            </li>
          )}

          {items.map((item, i) => {
            const heading = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;

            return (
              <li key={item.id}>
                {heading && (
                  <p className="ff-eyebrow px-2.5 pb-1 pt-2.5">{heading}</p>
                )}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === cursor}
                  data-active={i === cursor}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => choose(item)}
                  className={clsx(
                    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm',
                    i === cursor
                      ? 'bg-[var(--ff-surface-raised)] text-[var(--ff-text)]'
                      : 'text-[var(--ff-text-muted)]',
                  )}
                >
                  <Icon name={item.icon} size={15} />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.hint && (
                    <span className="text-[11.5px] text-[var(--ff-text-faint)]">{item.hint}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/** Three states, matching the runtime: explicit light, explicit dark, or follow system. */
export function cycleTheme() {
  const root = document.documentElement;
  const current = root.getAttribute('data-theme');
  const next = current === 'dark' ? 'light' : current === 'light' ? null : 'dark';

  if (next) root.setAttribute('data-theme', next);
  else root.removeAttribute('data-theme');

  try {
    if (next) localStorage.setItem('ff.theme', next);
    else localStorage.removeItem('ff.theme');
  } catch { /* private window */ }
}

/** ⌘K anywhere, and the / shortcut people expect from a search field. */
export function usePaletteShortcut(onOpen: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpen();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpen]);
}
